import React, { useState, useEffect, useRef } from 'react';
import './TempMeasurement.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine } from '@fortawesome/free-solid-svg-icons';
import { faTemperatureFull } from '@fortawesome/free-solid-svg-icons';

const config = require('../config');
const STALE_THRESHOLD_MS = 15 * 60 * 1000;

const TempMeasurement = ({ header, measurement, addMeasurementToGraph, measurementsToShowInChart, showChartPoints, floorHeatingRelayValue, zwaveThermostatId, 
  setVillaFloorHeatingValue, selectedHeatingMode, showTempSettings, showHumidity, showBattery }) => {
  const [value, setValue] = useState();
  const [lastUpdated, setLastUpdated] = useState();
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [humidity, setHumidity] = useState();
  const [battery, setBattery] = useState();
  const [minValue, setMinValue] = useState('');
  const [targetValue, setTargetValue] = useState('');
  const [maxValue, setMaxValue] = useState('');
  const [minValueError, setMinValueError] = useState('');
  const [targetValueError, setTargetValueError] = useState('');
  const [maxValueError, setMaxValueError] = useState('');
  const [isTempGraphSelected, setIsTempGraphSelected] = useState(false);
  const [isHumidityGraphSelected, setIsHumidityGraphSelected] = useState(false);
  const [isBatteryGraphSelected, setIsBatteryGraphSelected] = useState(false);

  const prevMinValue = useRef(minValue);
  const prevTargetValue = useRef(targetValue);
  const prevMaxValue = useRef(maxValue);

  const token = localStorage.getItem('token');

  const fetchTemp = () => {
    fetch(config.serverUrl + '/lastmeasurement?measurement=' + measurement + 'Temp',  {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    .then(response => response.json())
    .then(data => {
        setValue(data['value'])
        setLastUpdated(data['time'])
    });
  };

  const fetchHumidity = () => {
    if (showHumidity) {
      const token = localStorage.getItem('token');
    
      fetch(config.serverUrl + '/lastmeasurement?measurement=' + measurement + 'Humidity',  {
          headers: {
            'Authorization': `Bearer ${token}`
          }
      })
      .then(response => response.json())
      .then(data => setHumidity(data['value']));
    }
  };

  const fetchBatteryVoltage = () => {
    if (showBattery) {
      const token = localStorage.getItem('token');
    
      fetch(config.serverUrl + '/lastmeasurement?measurement=' + measurement + 'Voltage',  {
          headers: {
            'Authorization': `Bearer ${token}`
          }
      })
      .then(response => response.json())
      .then(data => setBattery(data['value']));    
    }
  };

  const fetchAllData = () => {
    fetchTemp();
    fetchHumidity();
    fetchBatteryVoltage();
  }
  
  useEffect(() => {
    fetchTemp();
  }, [measurement]);

  useEffect(() => {
    fetchHumidity();
  }, [showHumidity]);

  useEffect(() => {
    fetchBatteryVoltage();
  }, [showBattery]);

  useEffect(() => {
    fetchAllData(); // Initial fetch
  
    const interval = setInterval(() => {
      fetchAllData();
      setCurrentTime(Date.now());
    }, 30000); // Fetch every 30 seconds

    return () => clearInterval(interval); // Cleanup interval on component unmount
  }, []);

useEffect(() => {
    if (showTempSettings) {
      const token = localStorage.getItem('token');
      
      if (selectedHeatingMode !== 'Manual') {
        fetch(config.serverUrl + '/temp-settings?measurement=' + measurement + 'TempSettings&heating-mode=' + selectedHeatingMode,  {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          })
          .then(response => response.json())
          .then(data => {
            setMinValue(data.minValue ?? '');
            setTargetValue(data.targetValue ?? '');
            setMaxValue(data.maxValue ?? '');
          });
      }
      else {
        setMinValue('');
        setTargetValue('');
        setMaxValue('');
      }
    }
  }, [showTempSettings, selectedHeatingMode]);

  const handleBlur = async () => {
    const token = localStorage.getItem('token');

    if (selectedHeatingMode === 'Manual')
      return;
    
    try {
      const minValueNum = parseFloat(minValue);
      const targetValueNum = parseFloat(targetValue);
      const maxValueNum = parseFloat(maxValue);      

      let hasError = false;

      if (isNaN(minValueNum) && minValue !== null && minValue !== '') {
        setMinValueError('Invalid number');
        hasError = true;
      } else {
        setMinValueError('');
      }

      if (isNaN(targetValueNum) && targetValue !== null && targetValue !== '') {  
        setTargetValueError('Invalid number');
        hasError = true;
      } else {
        setTargetValueError('');
      }

      if (isNaN(maxValueNum) && maxValue !== null && maxValue !== '') { 
        setMaxValueError('Invalid number');
        hasError = true;
      } else {
        setMaxValueError('');
      }
      
      if (minValueNum > targetValueNum) {
        setMinValueError('Min value must be less than target value');
        hasError = true;
      }

      if (targetValueNum > maxValueNum) {
        setTargetValueError('Target value must be less than max value');
        hasError = true;
      }

      if (hasError) {
        return;
      }

      if (minValue !== prevMinValue.current || targetValue !== prevTargetValue.current || maxValue !== prevMaxValue.current) {
        await fetch(config.serverUrl + '/temp-settings', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            measurement: measurement + 'TempSettings',
            heatingMode: selectedHeatingMode,
            minValue: minValue ?? '',
            targetValue: targetValue ?? '',
            maxValue: maxValue ?? ''
          })
        });
        prevMinValue.current = minValue;
        prevTargetValue.current = targetValue;
        prevMaxValue.current = maxValue;
      }
    } catch (error) {
      console.error('Error updating temperature settings:', error);
    }
  };

  const refreshGraphIsSelected = () => {
    setIsTempGraphSelected(measurementsToShowInChart !== undefined && measurementsToShowInChart.filter(m => m.measurement === measurement + 'Temp').length > 0);
    setIsHumidityGraphSelected(measurementsToShowInChart !== undefined && measurementsToShowInChart.filter(m => m.measurement === measurement + 'Humidity').length > 0);
    setIsBatteryGraphSelected(measurementsToShowInChart !== undefined && measurementsToShowInChart.filter(m => m.measurement === measurement + 'Voltage').length > 0);
  };

  useEffect(() => {    
    refreshGraphIsSelected();
   }, [measurementsToShowInChart]);

   const handleFloorHeatingRelayToggle = () => {
    setVillaFloorHeatingValue(floorHeatingRelayValue === 'ON' ? 'OFF' : 'ON');
   }

  const handleAddMeasurementToGraph = (measurementType) => {
    addMeasurementToGraph(header, measurement + measurementType, showChartPoints ?? true, measurementType);
    refreshGraphIsSelected();
  };

  const handleMinValueChange = (e) => setMinValue(e.target.value);

  const handleTargetValueChange = (e) => {
    var oldTargetValue = parseFloat(targetValue);
    var newTargetValue = parseFloat(e.target.value);
    if (!isNaN(oldTargetValue) && !isNaN(newTargetValue)) {
      var diff = newTargetValue - oldTargetValue;
      var oldMinValue = parseFloat(minValue);
      if (!isNaN(oldMinValue))
        setMinValue(oldMinValue + diff);        
      var oldMaxValue = parseFloat(maxValue);
      if (!isNaN(oldMaxValue))
        setMaxValue(oldMaxValue + diff);
    }
    setTargetValue(e.target.value);
  }

  const handleMaxValueChange = (e) => setMaxValue(e.target.value);

  const parsedLastUpdated = Date.parse(lastUpdated);
  const isValueStale = Number.isFinite(parsedLastUpdated) && currentTime - parsedLastUpdated > STALE_THRESHOLD_MS;

  return (
    <div className="input-container">
      <span className="header">{header}</span>
      {floorHeatingRelayValue &&
        <button onClick={handleFloorHeatingRelayToggle} className={floorHeatingRelayValue === 'ON' ? 'floorheating-btn' : 'floorheating-btn-selected'}>
          <FontAwesomeIcon icon={faTemperatureFull} />
        </button>
      }
      <input type="text" value={value ?? ''} title={`Last updated: ${lastUpdated}`} readOnly className={`input-field ${isValueStale ? 'input-field-stale' : ''}`} />
      <button onClick={() => handleAddMeasurementToGraph('Temp')} className={isTempGraphSelected ? 'graph-btn-selected' : 'graph-btn'}>
        <FontAwesomeIcon icon={faChartLine} />
      </button>
      {showHumidity &&
        <>
          <input type="text" value={humidity ?? ''} readOnly className='input-field' />
          <button onClick={() => handleAddMeasurementToGraph('Humidity')} className={isHumidityGraphSelected ? 'graph-btn-selected' : 'graph-btn'}>
            <FontAwesomeIcon icon={faChartLine} />
          </button>
        </>
      }
      {showBattery &&
        <>
          <input type="text" value={battery ?? ''} readOnly className='input-field' />
          <button onClick={() => handleAddMeasurementToGraph('Voltage')} className={isBatteryGraphSelected ? 'graph-btn-selected' : 'graph-btn'}>
            <FontAwesomeIcon icon={faChartLine} />
          </button>
        </>
      }
      {showTempSettings && (floorHeatingRelayValue || zwaveThermostatId) &&
        <>
          <input type="text" placeholder="min" value={minValue ?? ''} onChange={handleMinValueChange} onBlur={handleBlur} className={`temp-measurement-input-field ${minValueError ? 'temp-measurement-error-input' : ''}`} data-error={minValueError}/>
          <input type="text" placeholder="target" value={targetValue ?? ''} onChange={handleTargetValueChange} onBlur={handleBlur} className={`temp-measurement-input-field ${targetValueError ? 'temp-measurement-error-input' : ''}`} data-error={targetValueError}/>
          <input type="text" placeholder="max" value={maxValue ?? ''} onChange={handleMaxValueChange} onBlur={handleBlur} className={`temp-measurement-input-field ${maxValueError ? 'temp-measurement-error-input' : ''}`} data-error={maxValueError}/>
        </>
      }
    </div>
  );
};

export default TempMeasurement;
